import React, { useState, useEffect, useRef, useCallback } from "react";
import { IconButton, Tooltip, Box, Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import "./Users.css";
import ExcelIcon from "../../assets/images/ExcelExport.png";
import {
  deleteUser,
  getUsersData,
  userActivityStatus,
} from "../../api/usersApi/usersApi";
import { UsersModel, UsersModelInfo } from "../../types/UsersModel";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridReadyEvent } from "ag-grid-enterprise";
import { Col, Row } from "react-bootstrap";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import { getExcelData } from "../../api/groupsApi/groupsApi";
import { ExcelExportPayload } from "../../types/GroupsModel";
import { useNavigate } from "react-router-dom";
import CustomLoadingOverlay from "../CustomLoadingOverlay/CustomLoadingOverlay ";
import { appSettings } from "../../config/appSetting";

import { useSnackbar } from "../Common/SnackbarContext";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { FormatDate } from "../Common/CommonMethods";
import { useLoader } from "../../context/LoaderContext";
import isPagePermission from "../../utils/common";
import LocalStorageUtil from "../../utils/localStorage";

interface UserTableProps {
  businessUnitData: any;
}

const UserTable: React.FC<UserTableProps> = ({ businessUnitData }) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showEmptySearchMessage, setShowEmptySearchMessage] = useState(false);
  const [rowData, setRowData] = useState<UsersModelInfo[]>();
  const gridRef = useRef<AgGridReact>(null);
  const navigation = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const { setLoading } = useLoader();
  const pageSizeSelector = appSettings.agGridPageSizeSelector;
  const defaulPageSize = appSettings.agGridDefaulPageSize;
  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    enableRowGroup: true,
  };
  const handleConfirmDialogClose = async (confirmed: boolean) => {
    setConfirmDialogOpen(false);
    if (confirmed && pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const Actions = (props: any) => {
    if (props.data === undefined) {
      return "";
    }
    const handleEditAction = () => {
      const selectedRowData = props.data;
      navigation("/userEditAction", {
        state: { rowData: selectedRowData, action: "Edit" },
      });
    };
    const handleDuplicateAction = () => {
      const selectedRowData = props.data;
      navigation("/userEditAction", {
        state: { rowData: selectedRowData, action: "Duplicate" },
      });
    };

    const handleDeleteClick = async () => {
      const selectedRowData = props.data;
      setPendingAction(() => async () => {
        const payload = {
          ContextNameForUser: LocalStorageUtil.getItem("contextName"),
          objUserModel: {
            ...selectedRowData,
          },
          businessUnitId: Number(LocalStorageUtil.getItem("businessUnitId")),
          isSuperAdmin: LocalStorageUtil.getItem("isSuperAdmin"),
        };
        try {
          await deleteUser(payload);
          getData(userModel);
        } catch (error) {
          console.error("Error fetching data", error);
        }
      });
      setConfirmDialogOpen(true);
    };

    const handleActiveStatusClick = async () => {
      setLoading(true);
      const selectedRowData = props.data;
      const payload = {
        ContextNameForUser: LocalStorageUtil.getItem("contextName"),
        objUserModel: {
          ...selectedRowData,
          active: false,
        },
        businessUnitId: Number(LocalStorageUtil.getItem("businessUnitId")),
        isSuperAdmin: LocalStorageUtil.getItem("isSuperAdmin"),
      };
      try {
        await userActivityStatus(payload);
        showSnackbar("User Activated successfully");
        getData(userModel);
      } catch (error) {
        showSnackbar(
          "Please update the 'Expiry Date' to make the user active",
          true
        );
      } finally {
        setLoading(false);
      }
    };

    const handleDeactiveStatusClick = async () => {
      setLoading(true);
      const selectedRowData = props.data;
      const payload = {
        ContextNameForUser: LocalStorageUtil.getItem("contextName"),
        objUserModel: {
          ...selectedRowData,
          active: true,
        },
        businessUnitId: Number(LocalStorageUtil.getItem("businessUnitId")),
        isSuperAdmin: LocalStorageUtil.getItem("isSuperAdmin"),
      };
      try {
        await userActivityStatus(payload);
        showSnackbar("User DeActivated successfully");
        getData(userModel);
      } catch (error) {
        showSnackbar(
          "Please update the 'Expiry Date' to make the user active",
          true
        );
      } finally {
        setLoading(false);
      }
    };

    return (
      <>
        {isPagePermission("Allow Edit","Users",504) && (
          <Tooltip title="Click to edit">
            <EditIcon
              className="icon-button-other"
              onClick={handleEditAction}
              style={{ padding: "2px" }}
            />
          </Tooltip>
        )}
        {isPagePermission("Allow Duplicate","Users",505) && (
          <Tooltip title="Click to duplicate">
            <CopyAllIcon
              className="icon-button-other"
              onClick={handleDuplicateAction}
              style={{ padding: "2px" }}
            />
          </Tooltip>
        )}
        {props.data.active ? (
          <Tooltip title="Click to deactivate the user">
            <PlayCircleIcon
              className="icon-button-other"
              style={{ padding: "2px" }}
              onClick={handleDeactiveStatusClick}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Click to activate the group">
            <DoDisturbIcon
              className="icon-button-other"
              onClick={handleActiveStatusClick}
              style={{ padding: "0.2vh" }}
            />
          </Tooltip>
        )}
        <Tooltip title="Click to delete the user">
          <CancelIcon
            className="icon-button-other"
            style={{ padding: "2px" }}
            onClick={handleDeleteClick}
          />
        </Tooltip>
      </>
    );
  };

  const statusCellRenderer = (params: any) => {
    const isActive = params?.data?.active;
    if (isActive === true) {
      return "Active";
    } else if (isActive === false) {
      return "Inactive";
    } else {
      return "";
    }
  };

  const [columnDefs] = useState<ColDef[]>([
    {
      headerName: "#",
      width: 70,
      sortable: false,
      filter: false,
      resizable: false,
      colId: "rowIndex",
      valueGetter: (params) => {
        return params.node ? (params.node.rowIndex ?? 0) + 1 : 0;
      },
    },
    {
      headerName: "Email",
      field: "emailId",
      tooltipField: "emailId",
      width: 200,
      colId: "emailId",
    },
    {
      headerName: "User Name",
      field: "userName",
      tooltipField: "userName",
      width: 200,
      colId: "userName",
    },
    {
      headerName: "First Name",
      field: "firstName",
      tooltipField: "firstName",
      width: 200,
      colId: "firstName",
    },
    {
      headerName: "Last Name",
      field: "lastName",
      tooltipField: "lastName",
      width: 200,
      colId: "lastName",
    },
    {
      headerName: "Status",
      field: "status",
      tooltipField: "status",
      width: 150,
      colId: "status",
      cellRenderer: statusCellRenderer,
    },
    {
      headerName: "Customer",
      field: "acountName",
      tooltipField: "acountName",
      width: 150,
      colId: "acountName",
    },
    {
      headerName: "Primary Group",
      field: "primaryGroup",
      tooltipField: "primaryGroup",
      width: 200,
      colId: "primaryGroup",
    },
    {
      headerName: "Expire Date",
      field: "expireDate",
      tooltipField: "expireDate",
      width: 200,
      colId: "expireDate",
      cellStyle: (params: any) => {
        const currentDate = new Date();
        const expireDate = new Date(params?.value) as any;
        if (params?.value && !isNaN(expireDate)) {
          if (expireDate <= currentDate) {
            return { color: "black", backgroundColor: "#f9e65c" };
          }
        }
        return null;
      },
      valueGetter: (params: any) => {
        const expireDateValue = params?.data?.expireDate;
        if (!expireDateValue) return "";
        const expireDate = new Date(expireDateValue) as any;
        if (isNaN(expireDate)) return "";
        return `${FormatDate(expireDate)}`;
      },
      comparator: (valueA: any, valueB: any, nodeA: any, nodeB: any) => {
        const dateA = new Date(nodeA.data.expireDate);
        const dateB = new Date(nodeB.data.expireDate);
        return dateA.getTime() - dateB.getTime();
      },
    },
    {
      headerName: "Updated On",
      field: "lastModifiedDate",
      tooltipField: "lastModifiedDate",
      width: 200,
      colId: "lastModifiedDate",
      valueFormatter: (params) => {
        return params?.value ? FormatDate(params?.value) : "";
      },
    },
    {
      headerName: "Actions",
      field: "link",
      cellRenderer: Actions,
      lockPinned: true,
      suppressSizeToFit: true,
      width: 130,
      minWidth: 130,
      pinned: "right",
    },
  ]);

  let newPageSize = 25;
  let userModel: UsersModel = {
    AccountId: businessUnitData?.accountId || LocalStorageUtil.getItem("accountId"),
    IsSuperAdmin: 1,
    SearchText: "",
    businessUnitId:
      businessUnitData?.businessUnitId ||
      LocalStorageUtil.getItem("businessUnitId"),
    currentPage: 1,
    isexporttpExcel: false,
    noOfRecords: 500,
    pagename: "Users",
    sortOrder: 0,
  };

  const handleCloseSearch = async () => {
    setSearchTerm("");
    await getData(userModel);
  };

  const getData = async (updatedUserModel: UsersModel) => {
    const payload = {
      ContextNameForUser:
        businessUnitData?.contextName || LocalStorageUtil.getItem("contextName"),
      objUserModel: updatedUserModel,
    };
    try {
      const userData = await getUsersData(payload);
      setRowData(userData);
      if (userData && searchTerm) {
        showSnackbar(userData[0].recordCount + " records found.");
      }
    } catch (error) {
      console.error("Error fetching data", error);
    }
  };

  const params: ExcelExportPayload = {
    AccountId: businessUnitData?.accountId || LocalStorageUtil.getItem("accountId"),
    IsSuperAdmin: 1,
    SearchText: "",
    aliasname: "Users",
    businessUnitId:
      businessUnitData?.businessUnitId ||
      LocalStorageUtil.getItem("businessUnitId"),
    contextName:
      businessUnitData?.contextName || LocalStorageUtil.getItem("contextName"),
    isexporttpExcel: true,
    offset: appSettings.timezone,
    pagename: "Users",
    sortOrder: 0,
  };
  const excelData = async () => {
    try {
      const excelDataResponse = await getExcelData(params);
      showSnackbar("File downloaded successfully!");
      if (!excelDataResponse) {
        showSnackbar("No records Found!");
      }
      const blob = new Blob([excelDataResponse], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "UserData.xlsx");
      document.body.appendChild(link);
      link.click();
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error fetching Excel data", error);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    if (value) {
      setShowEmptySearchMessage(false);
    }
  };

  const handleSearchClick = async () => {
    if (!searchTerm) {
      setShowEmptySearchMessage(true);
      return;
    }
    let updatedUserModel: UsersModel = {
      ...userModel,
      SearchText: searchTerm,
    };
    await getData(updatedUserModel);
    setShowEmptySearchMessage(false);
  };

  useEffect(() => {
    getData(userModel);
  }, [newPageSize]);

  const handleCreate = () => {
    navigation("/userEditAction");
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearchClick();
    }
  };

  const onGridReady = (params: GridReadyEvent) => {
    setTimeout(() => {
      params.api.sizeColumnsToFit();
    }, 100);
  };

  //Quick Filter
  const onFilterTextBoxChanged = useCallback(() => {
    gridRef.current!.api.setGridOption(
      "quickFilterText",
      (document.getElementById("filter-text-box") as HTMLInputElement).value
    );
  }, []);

  return (
    <div>
      <NavigationBar
        menuTitle="Configuration"
        title="User Management"
        screenTitle="Users"
        text="Users"
        data-testid="navigation-bar"
      />
      <Row style={{ margin: "1vh" }}>
        <Col>
          <Box>
            <div className="searchContainer">
              <div className="searchWrapper">
                <div
                  style={{ display: "flex", alignItems: "center", gap: "5px" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid #a09d9d",
                      borderRadius: "4px",
                      overflow: "hidden",
                      height: "1.5em",
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Enter Keyword"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onKeyDown={handleSearchKeyPress}
                      className="searchInput"
                      style={{
                        width: "25em",
                        height: "2.5em",
                        fontSize: "11px",
                        paddingLeft: ".5em",
                        border: "none",
                      }}
                    />
                    <Tooltip title="Click to search">
                      <button
                        onClick={handleSearchClick}
                        className="searchButton"
                      >
                        <SearchIcon style={{ color: "#006ab6" }} />
                      </button>
                    </Tooltip>
                  </div>
                  {searchTerm && (
                    <Tooltip title="Click to reset search result">
                      <IconButton
                        onClick={handleCloseSearch}
                        style={{ padding: "4px" }}
                        data-testid="clear-search"
                      >
                        <CloseIcon
                          style={{ color: "var(--aggrid-text-color)" }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>

                <input
                  type="text"
                  id="filter-text-box"
                  placeholder="Quick Filter..."
                  style={{
                    width: "25em",
                    height: "2.5em",
                    fontSize: "11px",
                    paddingLeft: ".5em",
                    marginBottom: ".1em",
                    display: "none",
                  }}
                  onInput={onFilterTextBoxChanged}
                />
                {showEmptySearchMessage && (
                  <span className="errorMessage">
                    Please enter text for search
                  </span>
                )}
              </div>
              <div style={{ display: "flex", justifyContent: "end" }}>
                {isPagePermission("Allow Create New","Users",502) && (
                  <Button
                    variant="contained"
                    size="small"
                    className="button createnew-button"
                    onClick={handleCreate}
                  >
                    Create New
                  </Button>
                )}
                {isPagePermission("Allow Export To Excel","Users",503) && (
                  <button
                    onClick={excelData}
                    className="excel-button"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                    }}
                    data-testid="excel-button"
                  >
                    <img
                      src={ExcelIcon}
                      alt="Download Excel"
                      className="excel-icon"
                      style={{ marginTop: ".3em" }}
                    />
                  </button>
                )}
              </div>
            </div>
            <div className="rp-main-inner col-sm-12">
              <div className="rp-main-inner col-sm-12">
                <div
                  className="agGridContainer ag-theme-quartz-dark"
                  style={{
                    height: "73vh",
                    marginTop: ".1vh",
                    width: "100%",
                  }}
                >
                  <AgGridReact
                    columnDefs={columnDefs}
                    rowData={rowData}
                    pagination={true}
                    paginationPageSize={defaulPageSize}
                    paginationPageSizeSelector={pageSizeSelector}
                    rowGroupPanelShow="always"
                    pivotPanelShow="always"
                    gridOptions={{
                      rowSelection: { 
                        mode : "multiRow",
                        checkboxes: true,
                        headerCheckbox: true,
                        enableClickSelection: false,
                       },  
                    }}
                    defaultColDef={defaultColDef}
                    ref={gridRef}
                    overlayNoRowsTemplate={
                      rowData?.length === 0
                        ? '<span class="ag-overlay-loading-center">No rows to show</span>'
                        : ""
                    }
                    loadingOverlayComponent={CustomLoadingOverlay}
                    loading={!rowData}
                    onGridReady={onGridReady}
                  />
                </div>
              </div>
            </div>
            <ConfirmDialog
              open={confirmDialogOpen}
              onClose={handleConfirmDialogClose}
              title="Confirm user delete"
              content="Remove user from DocDistro?"
            />
          </Box>
        </Col>
      </Row>
    </div>
  );
};

export default UserTable;
